/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: Home Deferred Runtime
   Project: La Troupe
   Version: 1.4.0
   Scope: Home performance runtime
   ----------------------------------------------------------
   1. Editorial mega-menu CSS + product hydration load only
      when the user actually approaches/focuses the header.
   2. LLCL mega product imagery is rewritten to responsive
      Shopify CDN variants before original-size downloads.
   3. Below-fold editorial video sources are dehydrated on
      initial Home load and restored only when playback starts.
   4. Heavy below-fold LLCL interaction engines hydrate only
      when their sections approach the viewport.
   ========================================================== */
(() => {
  'use strict';

  const boot = document.querySelector('script[data-ll-home-runtime]');
  if (!boot) return;

  const desktop = window.matchMedia('(min-width: 700px)');
  const HEADER = '.ll-header--llcl';
  const MEGA_IMAGE_CLASS = 'll-mega-editorial-product__image';
  const MEGA_IMAGE_WIDTHS = [240, 320, 480, 640, 800];
  const MEGA_IMAGE_SIZES = '(min-width: 1440px) 360px, (min-width: 1000px) 300px, 240px';
  const VIDEO_SELECTOR = 'video[data-ll-video]';
  const DEFERRED_SECTION_SCRIPT = 'script[data-ll-home-deferred-section-src]';
  const DEFERRED_SECTION_ROOT_MARGIN = '1200px 0px';
  const sectionScriptLoads = new Set();
  let started = false;

  const cssUrls = [boot.dataset.megaCss, boot.dataset.megaC06bCss].filter(Boolean);
  const jsUrls = [boot.dataset.megaJs, boot.dataset.iconsJs].filter(Boolean);

  const withImageWidth = (raw, width) => {
    if (!raw || typeof raw !== 'string') return raw;

    try {
      const url = new URL(raw, window.location.href);
      const isShopifyImage =
        url.hostname === 'cdn.shopify.com' ||
        url.pathname.includes('/cdn/shop/') ||
        url.pathname.includes('/s/files/');

      if (!isShopifyImage) return raw;

      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return raw;
    }
  };

  const installMegaImageGuard = () => {
    if (window.__LLCL_MEGA_IMAGE_GUARD__) return;

    const proto = window.HTMLImageElement?.prototype;
    if (!proto) return;

    const descriptor = Object.getOwnPropertyDescriptor(proto, 'src');
    if (!descriptor?.get || !descriptor?.set || descriptor.configurable === false) return;

    window.__LLCL_MEGA_IMAGE_GUARD__ = true;

    Object.defineProperty(proto, 'src', {
      configurable: descriptor.configurable,
      enumerable: descriptor.enumerable,
      get: descriptor.get,
      set(value) {
        if (this.classList?.contains(MEGA_IMAGE_CLASS) && typeof value === 'string') {
          const optimized = withImageWidth(value, 480);

          if (optimized !== value) {
            const srcset = MEGA_IMAGE_WIDTHS
              .map((width) => `${withImageWidth(value, width)} ${width}w`)
              .join(', ');

            this.setAttribute('srcset', srcset);
            this.setAttribute('sizes', MEGA_IMAGE_SIZES);
            this.setAttribute('fetchpriority', 'low');
            this.dataset.llclMegaOptimized = '1';
            descriptor.set.call(this, optimized);
            return;
          }
        }

        descriptor.set.call(this, value);
      }
    });
  };

  const installVideoPayloadGuard = () => {
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      if (video.dataset.llclPayloadGuard === '1') return;

      const sources = Array.from(video.querySelectorAll('source[src]'));
      const directSrc = video.getAttribute('src');
      if (!sources.length && !directSrc) return;

      const nativePlay = video.play.bind(video);

      sources.forEach((source) => {
        const src = source.getAttribute('src');
        if (!src) return;
        source.dataset.llclSrc = src;
        source.removeAttribute('src');
      });

      if (directSrc) {
        video.dataset.llclSrc = directSrc;
        video.removeAttribute('src');
      }

      video.preload = 'none';
      video.dataset.llclPayloadGuard = '1';

      try {
        video.pause();
        video.load();
      } catch (_) {}

      const hydrateSources = () => {
        if (video.dataset.llclSourcesHydrated === '1') return;

        sources.forEach((source) => {
          const src = source.dataset.llclSrc;
          if (src) source.setAttribute('src', src);
        });

        const storedDirectSrc = video.dataset.llclSrc;
        if (storedDirectSrc) video.setAttribute('src', storedDirectSrc);

        video.dataset.llclSourcesHydrated = '1';
        video.load();
      };

      video.play = (...args) => {
        hydrateSources();
        return nativePlay(...args);
      };
    });
  };

  const loadCss = (href) => new Promise((resolve) => {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.llHomeDeferred = 'mega-css';
    link.addEventListener('load', resolve, { once: true });
    link.addEventListener('error', resolve, { once: true });
    document.head.appendChild(link);
  });

  const loadScript = (src) => new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.llHomeDeferred = 'mega-js';
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', resolve, { once: true });
    document.head.appendChild(script);
  });

  const loadSectionScript = (marker) => {
    if (!marker || marker.dataset.llLoaded === '1') return;

    const src = marker.dataset.llHomeDeferredSectionSrc;
    if (!src) return;

    marker.dataset.llLoaded = '1';

    if (sectionScriptLoads.has(src) || document.querySelector(`script[src="${src}"]`)) {
      return;
    }

    sectionScriptLoads.add(src);

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.llHomeDeferred = 'section-js';
    script.addEventListener('error', () => {
      sectionScriptLoads.delete(src);
      marker.dataset.llLoaded = '0';
    }, { once: true });
    document.head.appendChild(script);
  };

  const observeNearViewport = (root, onEnter) => {
    if (!root || typeof onEnter !== 'function') return;

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      onEnter();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        onEnter();
      },
      { rootMargin: DEFERRED_SECTION_ROOT_MARGIN }
    );

    observer.observe(root);
  };

  const installDeferredSectionScripts = () => {
    document.querySelectorAll(DEFERRED_SECTION_SCRIPT).forEach((marker) => {
      const selector = marker.dataset.llHomeDeferredSectionRoot;
      const root = selector ? document.querySelector(selector) : null;

      if (!root) {
        loadSectionScript(marker);
        return;
      }

      observeNearViewport(root, () => loadSectionScript(marker));
    });
  };

  const installDeferredInlineInitializers = () => {
    const iconsRoot = document.querySelector('[data-ll-icons-journey]');
    const initIcons = window.__LLCL_DEFERRED_ICONS_INIT__;

    if (!iconsRoot || typeof initIcons !== 'function') return;

    observeNearViewport(iconsRoot, () => {
      requestAnimationFrame(initIcons);
    });
  };

  const cleanup = () => {
    document.removeEventListener('pointerover', onHeaderIntent, true);
    document.removeEventListener('pointerdown', onHeaderIntent, true);
    document.removeEventListener('focusin', onHeaderIntent, true);
  };

  const start = async () => {
    if (started || !desktop.matches) return;
    started = true;
    cleanup();
    installMegaImageGuard();
    await Promise.all(cssUrls.map(loadCss));
    for (const src of jsUrls) await loadScript(src);
  };

  function onHeaderIntent(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(HEADER)) start();
  }

  installVideoPayloadGuard();
  installDeferredSectionScripts();
  installDeferredInlineInitializers();

  if (!desktop.matches) return;

  document.addEventListener('pointerover', onHeaderIntent, true);
  document.addEventListener('pointerdown', onHeaderIntent, true);
  document.addEventListener('focusin', onHeaderIntent, true);
})();
